import { formatCaseNumber } from "@/api/cases/caseNumber";
import type { CaseTableRow } from "@/api/caseTable/CaseTableApi";
import { CaseTableApi } from "@/api/caseTable/CaseTableApi";
import {
  DEFAULT_CASE_TABLE_DEFINITION,
  parseCaseTableDefinition,
  type CaseTableDefinition,
  type CaseTableStatus,
} from "@/api/caseTable/caseTableDefinition";
import {
  useCaseTable,
  useSavedCaseListViews,
  useToggleCaseBookmark,
} from "@/api/caseTable/useCaseTable";
import { queryKeys } from "@/api/queryKeys";
import { ANIMAL_STATUS_LABEL, labelOf } from "@/constants/uiLabels";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { routes } from "@/constants/routes";
import { getShadowStyle } from "@/theme/shadowStyle";
import { useTheme } from "@/theme/ThemeProvider";
import { formatDateDE } from "@/utils/isoDate";
import { AccordionSection } from "@/ui/components/AccordionSection";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { StatusChip, statusToneFor } from "@/ui/components/StatusChip";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { CaretDownIcon, CaretUpIcon, HeartIcon } from "phosphor-react-native";
import { useEffect, useMemo, useState } from "react";
import { Pressable, Text, useWindowDimensions, View } from "react-native";

const STATE_KEY = "ferina_case_table_definition";

const STATUS_CHIPS: CaseTableStatus[] = [
  "in_care",
  "hibernating",
  "released",
  "deceased",
];

function HeartButton({
  active,
  onPress,
}: {
  active: boolean;
  onPress: () => void;
}) {
  const { theme } = useTheme();
  return (
    <Pressable onPress={onPress} hitSlop={8} accessibilityLabel="Merkliste">
      <HeartIcon
        size={16}
        color={active ? theme.color.danger : theme.color.muted}
        weight={active ? "fill" : "regular"}
      />
    </Pressable>
  );
}

function TableHeader() {
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  if (width < WIDE_VIEWPORT_BREAKPOINT) return null;
  const cell = {
    color: theme.color.muted,
    fontSize: theme.fontSize.tiny,
    fontWeight: "700" as const,
    textTransform: "uppercase" as const,
    letterSpacing: 0.35,
  };
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: theme.space.md,
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: theme.color.border,
        backgroundColor: theme.color.bgStrong,
        gap: theme.space.sm,
      }}
    >
      <Text style={[cell, { width: 88 }]}>Nummer</Text>
      <Text style={[cell, { flex: 1.2 }]}>Name</Text>
      <Text style={[cell, { width: 110 }]}>Status</Text>
      <Text style={[cell, { width: 100 }]}>Aufnahme</Text>
      <Text style={[cell, { width: 72 }]}>Gewicht</Text>
      <Text style={[cell, { flex: 1 }]}>Pflegestelle</Text>
      <View style={{ width: 28 }} />
    </View>
  );
}

function CaseTableRowView({
  row,
  show,
  onOpen,
  onToggleBookmark,
}: {
  row: CaseTableRow;
  show: (col: string) => boolean;
  onOpen: () => void;
  onToggleBookmark: () => void;
}) {
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_VIEWPORT_BREAKPOINT;
  const number = formatCaseNumber(row.case_number);
  const weight =
    show("weight") && row.current_weight_grams != null
      ? `${row.current_weight_grams} g`
      : "–";
  const intake = show("created_at")
    ? row.created_at
      ? formatDateDE(row.created_at)
      : "–"
    : null;
  const place =
    show("place") && row.can_read_full ? row.current_place_name : null;

  if (!wide) {
    const extras = [
      show("status") ? labelOf(ANIMAL_STATUS_LABEL, row.status) : null,
      intake,
      weight !== "–" ? weight : null,
      show("care_site") ? row.care_site_name : null,
      place,
    ].filter(Boolean);
    return (
      <DenseRow
        title={`${number}${row.name ? ` · ${row.name}` : ""}`}
        meta={extras.join(" · ")}
        onPress={onOpen}
        trailing={
          <HeartButton active={row.bookmarked} onPress={onToggleBookmark} />
        }
      />
    );
  }

  return (
    <Pressable
      onPress={onOpen}
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: theme.space.md,
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: theme.color.border,
        gap: theme.space.sm,
        backgroundColor: theme.color.surface,
      }}
    >
      <Text
        style={{
          width: 88,
          color: theme.color.muted,
          fontWeight: "700",
          fontSize: theme.fontSize.tiny,
          fontVariant: ["tabular-nums"],
        }}
        numberOfLines={1}
      >
        {number}
      </Text>
      <Text
        style={{
          flex: 1.2,
          color: theme.color.text,
          fontWeight: "600",
          fontSize: theme.fontSize.small,
        }}
        numberOfLines={1}
      >
        {row.name?.trim() || "Unbenannt"}
      </Text>
      <View style={{ width: 110 }}>
        {show("status") ? (
          <StatusChip
            label={labelOf(ANIMAL_STATUS_LABEL, row.status)}
            tone={statusToneFor(row.status)}
            size="sm"
          />
        ) : null}
      </View>
      <Text
        style={{
          width: 100,
          color: theme.color.muted,
          fontSize: theme.fontSize.tiny,
        }}
        numberOfLines={1}
      >
        {intake ?? "–"}
      </Text>
      <Text
        style={{
          width: 72,
          color: theme.color.textSecondary,
          fontSize: theme.fontSize.small,
          fontWeight: "600",
          fontVariant: ["tabular-nums"],
        }}
        numberOfLines={1}
      >
        {weight}
      </Text>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            color: theme.color.textSecondary,
            fontSize: theme.fontSize.tiny,
          }}
          numberOfLines={1}
        >
          {show("care_site") ? row.care_site_name ?? "–" : "–"}
        </Text>
        {place ? (
          <Text
            style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}
            numberOfLines={1}
          >
            {place}
          </Text>
        ) : null}
      </View>
      <HeartButton active={row.bookmarked} onPress={onToggleBookmark} />
    </Pressable>
  );
}

export function CaseTableWorkspace() {
  const { theme } = useTheme();
  const router = useRouter();
  const qc = useQueryClient();
  const toggle = useToggleCaseBookmark();
  const { views, refetch: refetchViews } = useSavedCaseListViews();
  const [definition, setDefinition] = useState<CaseTableDefinition>(
    DEFAULT_CASE_TABLE_DEFINITION
  );
  const [ready, setReady] = useState(false);
  const [viewName, setViewName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [viewsOpen, setViewsOpen] = useState(false);
  const cardShadow = getShadowStyle(theme.cardShadow);

  useEffect(() => {
    AsyncStorage.getItem(STATE_KEY)
      .then((raw) => {
        if (!raw) return;
        try {
          setDefinition(parseCaseTableDefinition(JSON.parse(raw)));
        } catch {
          /* ignore broken local state */
        }
      })
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    void AsyncStorage.setItem(STATE_KEY, JSON.stringify(definition));
  }, [definition, ready]);

  const { rows, loading, error } = useCaseTable(definition);

  const grouped = useMemo(() => {
    if (definition.group_by !== "care_site") {
      return [{ key: "all", title: "Fälle", rows }];
    }
    const map = new Map<string, CaseTableRow[]>();
    for (const row of rows) {
      const key = row.care_site_id ?? "none";
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    }
    return [...map.entries()].map(([key, groupRows]) => ({
      key,
      title: groupRows[0]?.care_site_name ?? "Ohne Pflegestelle",
      rows: groupRows,
    }));
  }, [definition.group_by, rows]);

  const toggleStatus = (status: CaseTableStatus) => {
    setDefinition((prev) => {
      const has = prev.statuses.includes(status);
      const statuses = has
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses };
    });
  };

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) {
      setMessage("Namen für die Ansicht angeben.");
      return;
    }
    setMessage(null);
    try {
      await CaseTableApi.upsertSavedView({ name, definition });
      setViewName("");
      await refetchViews();
      void qc.invalidateQueries({ queryKey: queryKeys.caseTable.savedViews });
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  const show = (col: string) => definition.columns.includes(col as never);

  return (
    <View style={{ gap: theme.space.sm }}>
      <View
        style={{
          backgroundColor: theme.color.surface,
          borderRadius: theme.radius.md,
          borderWidth: 1,
          borderColor: theme.color.border,
          paddingVertical: theme.space.sm,
          paddingHorizontal: theme.space.md,
          gap: theme.space.sm,
          ...cardShadow,
        }}
      >
        <FloatingLabelInput
          label="Suche"
          value={definition.q}
          onChangeText={(q) => setDefinition((prev) => ({ ...prev, q }))}
        />
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
          }}
        >
          <Pressable
            onPress={() =>
              setDefinition((prev) => ({
                ...prev,
                statuses: ["in_care", "hibernating", "released", "deceased"],
              }))
            }
          >
            <StatusChip
              label="Alle"
              tone="primary"
              active={definition.statuses.length >= 4}
              size="sm"
            />
          </Pressable>
          {STATUS_CHIPS.map((status) => {
            const on = definition.statuses.includes(status);
            return (
              <Pressable key={status} onPress={() => toggleStatus(status)}>
                <StatusChip
                  label={labelOf(ANIMAL_STATUS_LABEL, status)}
                  tone={statusToneFor(status)}
                  active={on}
                  size="sm"
                />
              </Pressable>
            );
          })}
          <Pressable
            onPress={() =>
              setDefinition((prev) => ({
                ...prev,
                bookmarked: !prev.bookmarked,
              }))
            }
            style={{
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: theme.radius.sm,
              borderWidth: 1,
              borderColor: definition.bookmarked
                ? theme.color.danger
                : theme.color.border,
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              backgroundColor: definition.bookmarked
                ? theme.color.dangerBg
                : theme.color.surface,
            }}
          >
            <HeartIcon
              size={12}
              color={
                definition.bookmarked ? theme.color.danger : theme.color.muted
              }
              weight={definition.bookmarked ? "fill" : "regular"}
            />
            <Text
              style={{
                color: definition.bookmarked
                  ? theme.color.danger
                  : theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
              }}
            >
              Merkliste
            </Text>
          </Pressable>
        </View>

        <Pressable
          onPress={() => setViewsOpen((v) => !v)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Text
            style={{
              color: theme.color.muted,
              fontSize: theme.fontSize.tiny,
              fontWeight: "600",
            }}
          >
            Ansichten
            {views.length ? ` (${views.length})` : ""}
          </Text>
          {viewsOpen ? (
            <CaretUpIcon size={12} color={theme.color.muted} />
          ) : (
            <CaretDownIcon size={12} color={theme.color.muted} />
          )}
        </Pressable>
        {viewsOpen ? (
          <View style={{ gap: theme.space.sm }}>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: theme.space.sm,
                alignItems: "flex-end",
              }}
            >
              <View style={{ flex: 1, minWidth: 160 }}>
                <FloatingLabelInput
                  label="Ansicht speichern"
                  value={viewName}
                  onChangeText={setViewName}
                />
              </View>
              <HeaderAction label="Speichern" onPress={() => void saveView()} />
            </View>
            {views.length > 0 ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {views.map((v) => (
                  <Pressable
                    key={v.id}
                    onPress={() =>
                      setDefinition(parseCaseTableDefinition(v.definition))
                    }
                    style={{
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: theme.radius.sm,
                      backgroundColor: theme.color.bgStrong,
                    }}
                  >
                    <Text
                      style={{
                        color: theme.color.textSecondary,
                        fontWeight: "600",
                        fontSize: theme.fontSize.tiny,
                      }}
                    >
                      {v.name}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : (
              <EmptyState title="Noch keine gespeicherten Ansichten." />
            )}
          </View>
        ) : null}
      </View>

      {loading ? (
        <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
          Laden…
        </Text>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Keine Tiere für diesen Filter."
          hint="Statusfilter oder Suche anpassen."
        />
      ) : (
        grouped.map((group) => {
          const open = expanded[group.key] ?? true;
          return (
            <AccordionSection
              key={group.key}
              dense
              title={group.title}
              subtitle={`${group.rows.length}`}
              expanded={open}
              onToggle={() =>
                setExpanded((prev) => ({ ...prev, [group.key]: !open }))
              }
            >
              <View
                style={{
                  borderTopWidth: 0,
                  overflow: "hidden",
                  borderBottomWidth: 1,
                  borderColor: theme.color.border,
                }}
              >
                <TableHeader />
                {group.rows.map((row) => (
                  <CaseTableRowView
                    key={row.id}
                    row={row}
                    show={show}
                    onOpen={() =>
                      router.push(routes.myAnimal(row.id) as never)
                    }
                    onToggleBookmark={() => toggle.mutate(row.id)}
                  />
                ))}
              </View>
            </AccordionSection>
          );
        })
      )}
      {error ? (
        <Text style={{ color: theme.color.danger }}>
          {(error as Error).message}
        </Text>
      ) : null}
      {message ? (
        <Text style={{ color: theme.color.danger, fontSize: 13 }}>{message}</Text>
      ) : null}
    </View>
  );
}
